import { useCallback, useEffect, useRef, useState } from 'react'

export type CameraStatus =
  | 'idle'
  | 'requesting'
  | 'active'
  | 'denied'
  | 'unavailable'
  | 'ended'

export function useCameraStream(options: {
  enabled: boolean
  width?: number
  height?: number
  stopOnHidden?: boolean
}) {
  const { enabled, width = 640, height = 480, stopOnHidden = true } = options
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [status, setStatus] = useState<CameraStatus>('idle')
  const [error, setError] = useState<Error | null>(null)

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setStatus((s) => (s === 'active' || s === 'requesting' ? 'idle' : s))
  }, [])

  const start = useCallback(async () => {
    if (!enabled) return
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('unavailable')
      setError(new Error('getUserMedia not available'))
      return
    }
    setStatus('requesting')
    setError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: 'user',
          width: { ideal: width },
          height: { ideal: height },
        },
      })
      streamRef.current = stream
      stream.getVideoTracks().forEach((track) => {
        track.addEventListener('ended', () => {
          setStatus('ended')
        })
      })
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play().catch(() => undefined)
      }
      setStatus('active')
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e))
      setError(err)
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setStatus('denied')
      } else {
        setStatus('unavailable')
      }
    }
  }, [enabled, width, height])

  useEffect(() => {
    if (enabled) {
      void start()
    } else {
      stop()
    }
    return () => stop()
  }, [enabled, start, stop])

  useEffect(() => {
    if (!stopOnHidden) return
    const onVis = () => {
      if (document.hidden) {
        stop()
      } else if (enabled) {
        void start()
      }
    }
    const onPageHide = () => stop()
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [stopOnHidden, enabled, start, stop])

  return { videoRef, status, error, stop, restart: start }
}
